/**
 * Socket.IO Manager Singleton
 *
 * Module-level singleton that manages a single Engine.IO transport
 * with multiplexed namespace sockets. Lives outside React lifecycle —
 * survives route changes, re-renders, and token refreshes.
 *
 * Usage:
 *   import { initSocketManager, setToken, getNamespace } from '@bb/session-manager';
 *
 *   // On auth ready:
 *   initSocketManager();
 *
 *   // On token refresh:
 *   setToken(newToken);
 *
 *   // In components (via useSocket hook):
 *   getNamespace('/contracts');
 */
import { Manager } from 'socket.io-client';

let manager = null;
let currentToken = null;
const namespaces = new Map(); // key → socket instance

/**
 * Initialize the socket manager. Creates a Manager with autoConnect: false.
 * Safe to call multiple times — only the first call creates the Manager.
 */
export const initSocketManager = () => {
  if (manager) return;

  manager = new Manager(window.location.origin, {
    autoConnect: false,
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
    reconnectionAttempts: Infinity,
  });
};

/**
 * Update the token used for all future namespace handshakes.
 * Does NOT trigger reconnection — just updates the closure variable
 * so function-based auth returns the latest token.
 */
export const setToken = (token) => {
  currentToken = token;
};

/**
 * Get (or create) a namespace socket. Uses function-based auth so every
 * handshake (including reconnects) always sends the latest token.
 *
 * @param {string} path - Namespace path (e.g., '/contracts')
 * @param {Object} [query] - Optional query params (e.g., { modelId: '123' })
 * @returns {import('socket.io-client').Socket}
 */
export const getNamespace = (path, query) => {
  if (!manager) {
    // Lazy-initialize if not yet created (e.g., child effect runs before
    // GlobalEventManager's effect on tab duplication / fast hydration).
    // initSocketManager() is idempotent and only creates a Manager with
    // autoConnect: false — no connection is made until getNamespace creates
    // a namespace socket and calls socket.connect().
    initSocketManager();
  }

  const key = query ? `${path}?${JSON.stringify(query)}` : path;
  if (namespaces.has(key)) return namespaces.get(key);

  const socket = manager.socket(path, {
    auth: (cb) => cb({ token: currentToken }),
    ...(query && { query }),
  });

  namespaces.set(key, socket);
  socket.connect();

  return socket;
};

/**
 * Disconnect a specific namespace socket and remove it from the cache.
 * Use for dialog-scoped sockets (e.g., /jobs/training) that should
 * close when the dialog closes.
 */
export const disconnectNamespace = (path, query) => {
  const key = query ? `${path}?${JSON.stringify(query)}` : path;
  const socket = namespaces.get(key);
  if (socket) {
    socket.disconnect();
    namespaces.delete(key);
  }
};

/**
 * Disconnect all namespace sockets and close the Manager transport.
 * Call on logout to ensure complete cleanup.
 */
export const disconnectAll = () => {
  namespaces.forEach((socket) => {
    try {
      socket.disconnect();
    } catch (e) {
      // Ignore cleanup errors
    }
  });
  namespaces.clear();
  if (manager) {
    try {
      // socket.io-client v4 exposes both .close() and .disconnect()
      if (typeof manager.close === 'function') {
        manager.close();
      } else if (typeof manager.disconnect === 'function') {
        manager.disconnect();
      }
    } catch (e) {
      // Ignore cleanup errors — logout must not be blocked by socket teardown
    }
    manager = null;
  }
  currentToken = null;
};

/**
 * One-shot download via socket namespace. Creates a namespace, emits
 * 'download' with the given id, waits for the callback response,
 * then disconnects the namespace.
 *
 * @param {string} endpoint - Namespace path (e.g., '/trainingmodels/download')
 * @param {string} id - The resource ID to download
 * @param {number} [timeoutMs=60000] - Timeout in milliseconds
 * @returns {Promise<any>} The data returned from the server callback
 */
export const downloadViaSocket = (endpoint, id, timeoutMs = 60000) => {
  return new Promise((resolve, reject) => {
    let settled = false;
    const socket = getNamespace(endpoint);

    const cleanup = () => {
      clearTimeout(timeout);
      socket.off('connect_error', onError);
      disconnectNamespace(endpoint);
    };

    const onError = (error) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error(`Download connection failed: ${error.message}`));
    };

    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error('Download timeout'));
    }, timeoutMs);

    socket.on('connect_error', onError);

    socket.emit('download', id, (data) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(data);
    });
  });
};
